/* eslint-disable ts/no-require-imports */

'use client'

import type { Block, ExtendedRecordMap } from 'notion-types'
import { useTheme } from 'next-themes'
import Link from 'next/link'
import { getPageTitle } from 'notion-utils'
import { useEffect } from 'react'
import { NotionRenderer as NotionRendererComponent } from 'react-notion-x'
import { Code } from 'react-notion-x/build/third-party/code'
import { Collection } from 'react-notion-x/build/third-party/collection'
import { Equation } from 'react-notion-x/build/third-party/equation'
import { Modal } from 'react-notion-x/build/third-party/modal'
import twemoji from 'twemoji'
import { cn, getCdnPath } from '~/lib/utils'

require('prismjs/components/prism-markup-templating.js')
require('prismjs/components/prism-markup.js')
require('prismjs/components/prism-bash.js')
require('prismjs/components/prism-c.js')
require('prismjs/components/prism-cpp.js')
require('prismjs/components/prism-csharp.js')
require('prismjs/components/prism-docker.js')
require('prismjs/components/prism-java.js')
require('prismjs/components/prism-js-templates.js')
require('prismjs/components/prism-coffeescript.js')
require('prismjs/components/prism-diff.js')
require('prismjs/components/prism-git.js')
require('prismjs/components/prism-go.js')
require('prismjs/components/prism-graphql.js')
require('prismjs/components/prism-handlebars.js')
require('prismjs/components/prism-less.js')
require('prismjs/components/prism-makefile.js')
require('prismjs/components/prism-markdown.js')
require('prismjs/components/prism-objectivec.js')
require('prismjs/components/prism-ocaml.js')
require('prismjs/components/prism-python.js')
require('prismjs/components/prism-reason.js')
require('prismjs/components/prism-rust.js')
require('prismjs/components/prism-sass.js')
require('prismjs/components/prism-scss.js')
require('prismjs/components/prism-solidity.js')
require('prismjs/components/prism-sql.js')
require('prismjs/components/prism-stylus.js')
require('prismjs/components/prism-swift.js')
require('prismjs/components/prism-wasm.js')
require('prismjs/components/prism-yaml.js')

interface NotionRendererProps {
  recordMap: ExtendedRecordMap
  route?: string
  rootPageId?: string
  className?: string
}

export function NotionRenderer(props: NotionRendererProps) {
  const { theme } = useTheme()

  useEffect(() => {
    // замена системных emoji на twemoji (как в Notion)
    const triggerTwemoji = () => {
      return twemoji.parse(document.body, {
        base: 'https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/',
      })
    }

    triggerTwemoji()
    const timerId = setInterval(triggerTwemoji, 5000)
    return () => clearInterval(timerId)
  }, [])

  const customMapImageUrl = (url: string | undefined, block: Block) => {
    if (!url) {
      console.error('[customMapImageUrl] URL can\'t be empty, so fallback to default', url)
      return getCdnPath('/assets/default.png')
    }

    if (url.startsWith('data:')) {
      return url
    }

    // more recent versions of notion don't proxy unsplash images
    if (url.startsWith('https://images.unsplash.com')) {
      return url
    }

    try {
      const targetURL = new URL(url)

      if (
        targetURL.pathname.startsWith('/secure.notion-static.com')
        && targetURL.hostname.endsWith('.amazonaws.com')
        && targetURL.searchParams.has('X-Amz-Credential')
        && targetURL.searchParams.has('X-Amz-Signature')
        && targetURL.searchParams.has('X-Amz-Algorithm')
      ) {
        // if the URL is already signed, then use it as-is
        url = targetURL.origin + targetURL.pathname
      }
    }
    catch {
      // ignore invalid urls
    }

    if (url.startsWith('/images')) {
      url = `https://www.notion.so${url}`
    }

    url = `https://www.notion.so${url.startsWith('/image') ? url : `/image/${encodeURIComponent(url)}`}`

    const notionImageUrlV2 = new URL(url)

    let table = block.parent_table === 'space' ? 'block' : block.parent_table
    if (table === 'collection' || table === 'team') {
      table = 'block'
    }

    notionImageUrlV2.searchParams.set('table', table)
    notionImageUrlV2.searchParams.set('id', block.id)
    notionImageUrlV2.searchParams.set('cache', 'v2')

    return `/api/get-private-image/${notionImageUrlV2.toString()}`
  }

  if (!props.recordMap) {
    return null
  }

  const title = getPageTitle(props.recordMap)

  return (
    <>
      <title>{title}</title>

      <NotionRendererComponent
        bodyClassName="py-0"
        className={cn(props.className, 'relative z-0 -mt-1 w-full pb-8')}
        components={{
          nextLink: Link,
          Code,
          Collection,
          Equation,
          Modal,
        }}
        darkMode={theme === 'dark'}
        disableHeader
        fullPage
        hideBlockId
        mapImageUrl={customMapImageUrl}
        mapPageUrl={targetURL => (props.route ? `/plus/${props.route}/${targetURL}` : targetURL)}
        recordMap={props.recordMap}
        rootPageId={props.rootPageId}
      />
    </>
  )
}
